// Desktop input layer: the mouse → synthetic-controller-0 pointer, per-tool
// desktop drag updates (marquee/extrude/box/freeform/resize/move), desktop
// press/release routing, reference-image dragging, the model + plan corner
// nav gizmos, and the view-align / orthographic camera tweens.
//
// Extracted from main.ts. Host dependencies come in through DesktopInputCtx;
// everything main.ts needs back is in the returned API. Keyboard dispatch and
// the shared gesture machinery (runDragHandlers, applyToolAction) stay in
// main.ts.
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { Line2 } from "three/addons/lines/Line2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";

import { Orbit3D } from "../view/orbit3d";
import { ReferenceImage } from "../view/reference-image";
import { CELL_SIZE, VoxelGrid } from "../model/voxel-grid";
import {
  type ControllerRay,
  hideControllerRay,
  setControllerRayOffCanvas,
  updateControllerRayVisuals,
} from "../view/controller-ray";
import {
  type CellBounds,
  type CellBox,
  type PickTarget,
} from "../interaction/pick";
import {
  controllerStates,
  controllers,
} from "../input/controllers";
import {
  consumeDesktopPress,
  consumeDesktopRelease,
  desktopMouse,
  installDesktopMarqueeRect,
} from "../input/desktop";
import { uiState } from "../ui/state";
import {
  applyExtrudeTargetDepth,
} from "../interaction/handlers/extrude";
import {
  setBoxFarCorner,
} from "../interaction/handlers/box";
import {
  setCircleRim,
} from "../interaction/handlers/circle";
import {
  beginFreeformPoly,
  cancelFreeformPoly,
  dropFreeformVertex,
  endFreeformDrag,
  freeformDragVert,
  freeformHoverVertex,
  freeformPolyActive,
  freeformPolyGroup,
  freeformPolyPlane,
  freeformPress,
  freeformPreviewPositions,
  freeformVertexPositions,
  moveFreeformVertex,
  setFreeformCursor,
} from "../interaction/handlers/freeform";
import {
  setMoveOffset,
} from "../interaction/handlers/move";
import { interaction } from "../interaction/state";
import {
  type SceneObject,
} from "../model/scene-object";
import { Selection } from "../model/selection";
import {
  WallSystem,
} from "../floor/walls";
import { PlacementSystem } from "../floor/placement";
import {
  commitSelectionChange,
} from "../model/history";
import {
  isInsideGizmo,
  pickGizmoAxis,
  setGizmoHover,
} from "../ui/gizmo";

// Everything the desktop input layer needs from the host app. The pick/ray
// state (lastPick, controllerRays) is the same synthetic-controller-0 slot the
// VR layer fills from real controllers. Mutable host flags (model ortho, the
// pending paste float, plan-align in-flight, reference editing) come in as
// getter/setter pairs since `let` bindings can't be shared across modules.
export interface DesktopInputCtx {
  camera: THREE.PerspectiveCamera;
  orthoCamera: THREE.OrthographicCamera;
  orbitControls: Orbit3D;
  floor2DControls: OrbitControls;
  gridGroup: THREE.Group;
  grid: VoxelGrid;
  selection: Selection;
  cellBounds: CellBounds;
  referenceImage: ReferenceImage;
  lastPick: (PickTarget | null)[];
  controllerRays: ControllerRay[];
  resizeHandleMeshes: THREE.Mesh[];
  resizeHotHandle: number[];
  resizeHotDist: number[];
  desktopMarqueeRect: ReturnType<typeof installDesktopMarqueeRect>;
  activeObject: SceneObject;
  placementSystem: PlacementSystem;
  wallSystem: WallSystem;
  MODEL_AXES: THREE.Vector3[];
  PLAN_VIEW_DIST: number;
  desktopUI: { render: () => void };
  activeCamera: () => THREE.Camera;
  gestureObject: () => SceneObject;
  gestureRaycastMasks: (i: number) => {
    skip: ReadonlySet<string> | undefined;
    include: ReadonlySet<string> | undefined;
    includeBox: CellBox | null;
    skipBox?: CellBox | null;
  };
  multiObjectPick: (
    i: number,
    originWorld: THREE.Vector3,
    dirWorld: THREE.Vector3,
    skip: ReadonlySet<string> | undefined,
    include: ReadonlySet<string> | undefined,
    includeBox: CellBox | null,
    skipBox?: CellBox | null,
  ) => { pick: PickTarget; group: THREE.Object3D };
  grabbedHandleIndex: (axis: 0 | 1 | 2, sign: -1 | 1) => number;
  mirrorExpandSelection: (
    cells: Iterable<{ ix: number; iy: number; iz: number }>,
  ) => { ix: number; iy: number; iz: number }[];
  selectionStateString: () => string;
  commitReference: () => void;
  beginGridAction: (i: number) => void;
  finalizeInteraction: (i: number) => void;
  // Freeform polygon lasso (built outside the gesture seam): resolve which
  // object the first vertex draws into (null if the cursor isn't over one), and
  // close the loop — fill the enclosed area inside one transaction + select it.
  freeformResolveTarget: (
    i: number,
  ) => { grid: VoxelGrid; group: THREE.Object3D } | null;
  closeFreeform: () => void;
  applyResizeExtent: (axis: 0 | 1 | 2, sign: -1 | 1, edge: number) => void;
  nearestAxisIn: (dir: THREE.Vector3, axes: THREE.Vector3[]) => THREE.Vector3;
  planUpForAxis: (axis: THREE.Vector3, out: THREE.Vector3) => void;
  snapUpForAxis: (
    axis: THREE.Vector3,
    currentUp: THREE.Vector3,
    out: THREE.Vector3,
  ) => void;
  isShiftHeld: () => boolean;
  frontLayerOn: () => boolean;
  modelOrthoOn: () => boolean;
  setModelOrthoFlag: (on: boolean) => void;
  referenceEditingOn: () => boolean;
  planAlignOn: () => boolean;
  setPlanAlign: (on: boolean) => void;
  getSelFloat: () => { selVersion: number } | null;
  clearSelFloat: () => void;
  // Eyedropper: if the color editor has armed a sample, consume this LMB press
  // by reading the clicked voxel's stored color (returns true to swallow it so
  // no tool acts). Returns false when nothing is armed — the press proceeds.
  tryEyedropSample: () => boolean;
}

export function installDesktopInput(ctx: DesktopInputCtx) {
  const {
    camera,
    orthoCamera,
    orbitControls,
    floor2DControls,
    gridGroup,
    grid,
    selection,
    cellBounds,
    referenceImage,
    lastPick,
    controllerRays,
    resizeHandleMeshes,
    resizeHotHandle,
    resizeHotDist,
    desktopMarqueeRect,
    activeObject,
    placementSystem,
    wallSystem,
    MODEL_AXES,
    PLAN_VIEW_DIST,
    desktopUI,
    activeCamera,
    gestureObject,
    gestureRaycastMasks,
    multiObjectPick,
    grabbedHandleIndex,
    mirrorExpandSelection,
    selectionStateString,
    commitReference,
    beginGridAction,
    finalizeInteraction,
    applyResizeExtent,
    nearestAxisIn,
    planUpForAxis,
    snapUpForAxis,
    isShiftHeld,
    frontLayerOn,
  } = ctx;

  // Private scratch (write-before-read at every use site; the VR layer and
  // main keep their own copies).
  const _pickOriginTmp = new THREE.Vector3();
  const _pickDirTmp = new THREE.Vector3();
  const _panelRaycaster = new THREE.Raycaster();
  const _offCanvasPosTmp = new THREE.Vector3();
  const _camWorldPosTmp = new THREE.Vector3();
  const _layoutBox = new THREE.Box3();

const _dtForward = new THREE.Vector3(0, 0, -1);
const _dtOrigin = new THREE.Vector3();
const _dtDir = new THREE.Vector3();

// Cursor → reference-image plane, in the image's FRAME-local coords (where its
// drag/handle math lives). Build the world mouse ray, pull it into gridGroup-
// local, and let the image project it onto its (view-aligned) plane. Null if it
// misses.
const _riOrigin = new THREE.Vector3();
const _riDir = new THREE.Vector3();
const _riRay = new THREE.Ray();
const _riInv = new THREE.Matrix4();
function referenceLocalPoint(): { x: number; z: number } | null {
  desktopMouseRay(_riOrigin, _riDir);
  gridGroup.updateMatrixWorld();
  _riInv.copy(gridGroup.matrixWorld).invert();
  _riRay.origin.copy(_riOrigin);
  _riRay.direction.copy(_riDir);
  _riRay.applyMatrix4(_riInv); // world ray → gridGroup-local
  return referenceImage.projectRay(_riRay);
}

// Reference-edit drag: press/release are routed in onDesktopPress/Release (so the
// nav gizmo keeps priority); this just applies the in-flight transform each frame.
let _refDragging = false;
function applyReferenceDrag(): void {
  if (!_refDragging || !desktopMouse.lmb) return;
  const lp = referenceLocalPoint();
  // Rotation snaps to 15° unless Shift is held (then it's continuous).
  if (lp) referenceImage.applyDrag(lp.x, lp.z, !isShiftHeld());
}

// Build the mouse's world ray (camera through the cursor NDC) into out vars.
// Perspective: rays fan out from the eye. Orthographic: rays are parallel, so
// the origin is the cursor point on the near plane and the direction is the
// fixed camera forward — otherwise picking wouldn't match the flat view.
const _dmrTmp = new THREE.Vector3();
function desktopMouseRay(origin: THREE.Vector3, dir: THREE.Vector3): void {
  const cam = activeCamera();
  if (cam instanceof THREE.OrthographicCamera) {
    origin.set(desktopMouse.ndc.x, desktopMouse.ndc.y, -1).unproject(cam);
    cam.getWorldDirection(dir);
    // The ortho near plane sits ~camera.far behind the eye, so this origin is
    // thousands of cells from the scene — past the voxel raycast's DDA budget,
    // so picks miss. Slide it forward along the ray to ~20 units before the orbit
    // target (the scene), keeping the lateral offset, so the march starts at the
    // voxels.
    const ahead = _dmrTmp.copy(orbitControls.target).sub(origin).dot(dir) - 20;
    if (ahead > 0) origin.addScaledVector(dir, ahead);
    return;
  }
  cam.getWorldPosition(origin);
  dir
    .set(desktopMouse.ndc.x, desktopMouse.ndc.y, 0.5)
    .unproject(cam)
    .sub(origin)
    .normalize();
}

// Pose synthetic controller 0 to reproduce the mouse ray (so every ray-based
// helper — controllerEffectiveWorldPos(0)/laserLocalDir(0) — works), mirror
// LMB → selecting, and route press/release into the shared grid-action seam.
function updateDesktopPointer(): void {
  desktopMouseRay(_dtOrigin, _dtDir);
  const c = controllers[0];
  if (c) {
    // XRTargetRaySpace ships with matrixAutoUpdate=false (its matrix is
    // normally driven by the WebXR pose). On desktop we pose it from the
    // mouse, so re-enable auto-update or the matrix never recomposes from
    // position/quaternion and the ray stays frozen (breaks ray-based tools).
    c.matrixAutoUpdate = true;
    c.position.copy(_dtOrigin);
    c.quaternion.setFromUnitVectors(_dtForward, _dtDir);
    c.updateMatrixWorld(true);
  }
  controllerStates[0].selecting = desktopMouse.lmb;
  if (consumeDesktopPress()) onDesktopPress();
  if (consumeDesktopRelease()) onDesktopRelease();
}

// Screen-rectangle marquee (the Select tool on desktop). _dmActive while
// dragging a box; the selection is recomputed live each frame. Holding shift
// (add) or ctrl/cmd (subtract) at press composes against the selection as it
// was when the gesture began (_dmBase), so the live preview is stable.
const _dmStartNdc = new THREE.Vector2();
const _mqTmp = new THREE.Vector3();
const _mqDir = new THREE.Vector3();
const _mqInv = new THREE.Matrix4();
let _dmActive = false;
let _dmAlt = false; // non-passthrough (Alt): keep only the front-most voxel layer
type DmMode = "replace" | "add" | "subtract";
let _dmMode: DmMode = "replace";
let _dmBase: { ix: number; iy: number; iz: number }[] = [];
// Selection state when the gesture began, so its net change can be recorded as
// one undo step on release (the marquee mutates the selection live, outside any
// transaction).
let _dmSelBefore = "";

// Under this press→release NDC distance a Select gesture is a CLICK, not a
// marquee drag — and a click on a voxel selects its whole connected object.
const SELECT_CLICK_NDC = 0.01;
const _OBJ_NB: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];

// All voxels face-connected to the seed in the active grid — the "object" you
// clicked. BFS over filled neighbours.
function connectedObject(
  sx: number,
  sy: number,
  sz: number,
): { ix: number; iy: number; iz: number }[] {
  if (!grid.has(sx, sy, sz)) return [];
  const out: { ix: number; iy: number; iz: number }[] = [];
  const seen = new Set<string>([`${sx},${sy},${sz}`]);
  const stack: [number, number, number][] = [[sx, sy, sz]];
  while (stack.length > 0) {
    const [x, y, z] = stack.pop()!;
    out.push({ ix: x, iy: y, iz: z });
    for (const [dx, dy, dz] of _OBJ_NB) {
      const nx = x + dx;
      const ny = y + dy;
      const nz = z + dz;
      const k = `${nx},${ny},${nz}`;
      if (seen.has(k) || !grid.has(nx, ny, nz)) continue;
      seen.add(k);
      stack.push([nx, ny, nz]);
    }
  }
  return out;
}

// Select the whole connected object at the clicked voxel, composing with the
// press modifier (replace / add / subtract) like the marquee does.
function selectClickedObject(ix: number, iy: number, iz: number): void {
  // Mirror also grabs the object's reflection on each enabled axis.
  const obj = mirrorExpandSelection(connectedObject(ix, iy, iz));
  if (obj.length === 0) return;
  const key = (c: { ix: number; iy: number; iz: number }) => `${c.ix},${c.iy},${c.iz}`;
  if (_dmMode === "add") {
    selection.replace([..._dmBase, ...obj]); // replace() dedupes by key
  } else if (_dmMode === "subtract") {
    const rm = new Set(obj.map(key));
    selection.replace(_dmBase.filter((c) => !rm.has(key(c))));
  } else {
    selection.replace(obj);
  }
}

function onDesktopPress(): void {
  // Eyedropper (color editor armed): this press samples the voxel under the
  // cursor instead of acting as a tool. Checked first so it wins over every
  // other LMB behaviour while armed; a miss is swallowed too (stays armed).
  if (ctx.tryEyedropSample()) return;

  // Navigation gizmo (top-right): press begins a gizmo interaction — drag to
  // orbit the view (like Blender), or release without moving to snap the view
  // down the clicked axis. Any press in the region is swallowed so it never
  // leaks into a tool action through the overlay.
  if (isInsideGizmo(desktopMouse.clientX, desktopMouse.clientY)) {
    _vaActive = false; // cancel any in-progress view-align
    _gizmoDrag.active = true;
    _gizmoDrag.moved = false;
    _gizmoDrag.startX = desktopMouse.clientX;
    _gizmoDrag.startY = desktopMouse.clientY;
    _gizmoDrag.lastX = desktopMouse.clientX;
    _gizmoDrag.lastY = desktopMouse.clientY;
    _gizmoDrag.axis = pickGizmoAxis(desktopMouse.clientX, desktopMouse.clientY);
    return;
  }

  // Reference-image edit mode: LMB grabs an image handle (the nav gizmo above
  // still wins, so view controls keep working); it never touches voxels.
  if (ctx.referenceEditingOn() && referenceImage.hasImage()) {
    const lp = referenceLocalPoint();
    if (lp) {
      const kind = referenceImage.pickHandle(lp.x, lp.z);
      if (kind) {
        referenceImage.beginDrag(kind, lp.x, lp.z);
        _refDragging = true;
      }
    }
    return;
  }

  // Select mode is purely selection: any press starts a screen-rectangle marquee
  // (a click without a drag selects the connected object on release). Moving is
  // the separate Move tool. Shift adds / Ctrl-Cmd subtracts; Alt = front layer.
  if (uiState.topMode === "select") {
    _dmMode = desktopMouse.pressSubtract
      ? "subtract"
      : desktopMouse.pressShift
        ? "add"
        : "replace";
    _dmAlt = desktopMouse.pressAlt || frontLayerOn(); // Alt or the toggle
    _dmActive = true;
    _dmSelBefore = selectionStateString();
    _dmStartNdc.copy(desktopMouse.ndc);
    _dmBase = _dmMode === "replace" ? [] : [...selection.entries()];
    return;
  }
  // Freeform polygon lasso (Sketch/Paint, Model mode): a multi-click outline,
  // NOT the single press-drag the other shapes use — so it bypasses the gesture
  // seam. Each press drops/grabs a vertex or closes the loop.
  if (freeformShapeActive()) {
    handleFreeformPress();
    return;
  }
  // Everything else (Move grabs a voxel, Resize a wall handle, the sketch tools)
  // goes through the shared gesture seam.
  beginGridAction(0);
}

function onDesktopRelease(): void {
  // End a gizmo interaction: a click (no drag) on an axis ball snaps the view.
  if (_gizmoDrag.active) {
    if (!_gizmoDrag.moved && _gizmoDrag.axis) startViewAlign(_gizmoDrag.axis);
    _gizmoDrag.active = false;
    _gizmoDrag.axis = null;
    return;
  }
  if (_refDragging) {
    referenceImage.endDrag();
    _refDragging = false;
    commitReference(); // one undo step per move/scale/rotate gesture
    return;
  }
  if (_dmActive) {
    _dmActive = false;
    desktopMarqueeRect.hide();
    // A click without dragging on a voxel selects that whole connected object,
    // instead of the single voxel the point-sized marquee would have caught.
    // Otherwise (a real drag, or a click on empty space) apply the box select
    // now — the selection isn't touched during the drag, only here on release.
    const pick = lastPick[0];
    if (
      _dmStartNdc.distanceTo(desktopMouse.ndc) < SELECT_CLICK_NDC &&
      pick?.kind === "voxel"
    ) {
      selectClickedObject(pick.ix, pick.iy, pick.iz);
    } else {
      commitDesktopMarquee();
    }
    // A floating selection survives add/subtract (re-pin its stash to the
    // now-current selection); a fresh REPLACE selection commits it where it sits.
    if (_dmMode === "replace") ctx.clearSelFloat();
    else {
      const sf = ctx.getSelFloat();
      if (sf) sf.selVersion = selection.version;
    }
    // Record the gesture's net selection change as one undo step.
    commitSelectionChange(_dmSelBefore, selectionStateString());
    return;
  }
  // End a freeform vertex drag (the polygon persists across clicks — only ESC /
  // Enter / a closing click end the whole gesture).
  if (freeformPolyActive()) {
    endFreeformDrag();
    return;
  }
  finalizeInteraction(0);
}

// Drag-the-gizmo-to-orbit (Blender-style). While the gizmo is grabbed, mouse
// movement orbits the camera around the orbit target by adjusting the spherical
// angles — the same mapping as an OrbitControls rotate, but driven from the
// corner gizmo. A press that never moves past the threshold stays a click
// (handled in onDesktopRelease as a view-snap).
const _gizmoDrag = {
  active: false,
  moved: false,
  startX: 0,
  startY: 0,
  lastX: 0,
  lastY: 0,
  axis: null as THREE.Vector3 | null,
};
const GIZMO_DRAG_THRESH = 5; // px before a press counts as a drag, not a click
function updateGizmoDrag(): void {
  if (!_gizmoDrag.active || !desktopMouse.lmb) return;
  const dx = desktopMouse.clientX - _gizmoDrag.lastX;
  const dy = desktopMouse.clientY - _gizmoDrag.lastY;
  _gizmoDrag.lastX = desktopMouse.clientX;
  _gizmoDrag.lastY = desktopMouse.clientY;
  if (
    !_gizmoDrag.moved &&
    Math.hypot(
      desktopMouse.clientX - _gizmoDrag.startX,
      desktopMouse.clientY - _gizmoDrag.startY,
    ) > GIZMO_DRAG_THRESH
  ) {
    _gizmoDrag.moved = true;
  }
  if (!_gizmoDrag.moved || (dx === 0 && dy === 0)) return;
  // Orthographic modeling is axis-snapped: dragging the gizmo neither free-orbits
  // nor snaps (now that `moved` is set, the release won't snap either). Only a
  // clean click snaps; only the right-drag swipe rotates. (Scoped to modeling so
  // the layout 3D gizmo, which shares this path, still free-orbits.)
  if (uiState.mode === "modeling" && ctx.modelOrthoOn()) return;
  // Trackball orbit via the same controller as RMB — over-pole, no clamp.
  // ~1° per pixel matches the old gizmo-drag feel.
  const prev = orbitControls.rotateSpeed;
  orbitControls.rotateSpeed = (2 * Math.PI) / 360;
  orbitControls.rotateByPixels(dx, dy);
  orbitControls.rotateSpeed = prev;
}

// Smoothly align the orbit view down a world axis (navigation-gizmo click).
// Slerps the camera around the orbit target at constant distance so it swings
// along an arc (no dive toward the target a position-lerp would cause).
const _vaFromDir = new THREE.Vector3();
const _vaDelta = new THREE.Quaternion();
const _vaQ = new THREE.Quaternion();
const _vaDir = new THREE.Vector3();
let _vaActive = false;
let _vaT = 0;
let _vaDist = 0;
function startViewAlign(axisDir: THREE.Vector3, up?: THREE.Vector3): void {
  const target = orbitControls.target;
  _vaDist = camera.position.distanceTo(target);
  if (_vaDist < 1e-4) _vaDist = 1;
  _vaFromDir.copy(camera.position).sub(target).normalize();
  _vaDelta.setFromUnitVectors(_vaFromDir, axisDir.normalize());
  // Choose the camera up: an explicit `up` keeps the swipe's roll; otherwise use
  // the canonical orientation (+Y for sides; ∓Z for top/bottom so +X reads
  // right). orbitControls re-derives the orientation from camera.up.
  if (up) camera.up.copy(up);
  else if (Math.abs(axisDir.y) > 0.9) camera.up.set(0, 0, axisDir.y > 0 ? -1 : 1);
  else camera.up.set(0, 1, 0);
  _vaT = 0;
  _vaActive = true;
}
function updateViewTween(): void {
  if (!_vaActive) return;
  _vaT = Math.min(1, _vaT + 0.14);
  const e = _vaT * _vaT * (3 - 2 * _vaT); // smoothstep
  _vaQ.identity().slerp(_vaDelta, e);
  _vaDir.copy(_vaFromDir).applyQuaternion(_vaQ);
  camera.position.copy(orbitControls.target).addScaledVector(_vaDir, _vaDist);
  // Re-aim now so the tween looks right even when the controls were already
  // updated this frame (the floor branch updates the camera before the tween).
  camera.lookAt(orbitControls.target);
  if (_vaT >= 1) _vaActive = false;
}

// Toggle the model's orthographic projection. Ortho is an axis-snapped view
// (like the 2D floor plan): right-drag swipes between axis-aligned views and the
// free RMB orbit is disabled. Turning it on snaps to the nearest axis so the
// view is square from the start.
const _orthoSnapDir = new THREE.Vector3();
const _orthoUp = new THREE.Vector3();
const _orthoScreenUp = new THREE.Vector3();
function setModelOrthographic(on: boolean): void {
  ctx.setModelOrthoFlag(on);
  orbitControls.rotateEnabled = !on;
  // Snap to the nearest axis so ortho opens square (modeling only — the
  // perspective camera is shared with the floor 3D view).
  if (on && uiState.mode === "modeling") {
    _orthoSnapDir.copy(camera.position).sub(orbitControls.target).normalize();
    const axis = nearestAxisIn(_orthoSnapDir, MODEL_AXES);
    // Top/bottom views: carry over the in-plane roll so whatever axis was
    // pointing up on screen in 3D keeps pointing up in 2D, instead of resetting
    // to the canonical +X-reads-right orientation. (Side views stay upright.)
    if (Math.abs(axis.y) > 0.9) {
      camera.updateMatrixWorld();
      _orthoScreenUp.setFromMatrixColumn(camera.matrixWorld, 1); // camera local +Y
      snapUpForAxis(axis, _orthoScreenUp, _orthoUp);
      startViewAlign(axis, _orthoUp);
    } else {
      startViewAlign(axis);
    }
  }
  desktopUI.render();
}

// Axis-aligned bounding box of the actual layout content (walls + placements),
// or null when the layout is empty. The floor scene's ground plane + grid are
// excluded (they'd otherwise dominate the box), so this is the "drawing", which
// is what every axis view re-centers and fits on. (_layoutBox and the
// fit scratch are declared up near the ortho camera for init order.)
function layoutBounds(): THREE.Box3 | null {
  _layoutBox.makeEmpty();
  placementSystem.group.updateMatrixWorld(true);
  wallSystem.expandBounds(_layoutBox); // real wall corners (not the preview/handles)
  placementSystem.expandBounds(_layoutBox);
  return _layoutBox.isEmpty() ? null : _layoutBox;
}

// Smoothly snap the 2D plan's ORTHOGRAPHIC camera to look straight down a world
// axis (plan-view nav-gizmo click or right-drag). Unlike the 3D view, the plan
// never free-orbits: every change lands on an axis-aligned orthographic view
// (top, bottom, or one of the four sides). The orbit target glides to the
// layout's bounding-box center, but the current zoom is KEPT (so changing axis
// doesn't reset however far you've zoomed in).
// (PLAN_VIEW_DIST is declared up near the ortho camera for init order.)
const _pvFromQ = new THREE.Quaternion();
const _pvToQ = new THREE.Quaternion();
const _pvToUp = new THREE.Vector3();
const _pvDir = new THREE.Vector3();
const _pvFromTarget = new THREE.Vector3();
const _pvToTarget = new THREE.Vector3();
let _pvFromZoom = 1;
let _pvToZoom = 1;
let _pvT = 0; // _planAlignActive is declared near the ortho camera (init order)
function startPlanViewAlign(axisDir: THREE.Vector3, up?: THREE.Vector3): void {
  _pvFromQ.copy(orthoCamera.quaternion);
  const dir = _pvDir.copy(axisDir).normalize();
  // Square the other axes to the screen — an explicit `up` keeps the swipe's
  // roll; otherwise reset to the canonical (north-up) orientation.
  if (up) _pvToUp.copy(up);
  else planUpForAxis(dir, _pvToUp);
  // Re-center on the layout and refit the zoom for this axis (no-op fallbacks
  // when the layout is empty: stay at the origin, keep the current zoom).
  const box = layoutBounds();
  _pvFromTarget.copy(floor2DControls.target);
  if (box) box.getCenter(_pvToTarget);
  else _pvToTarget.set(0, 0, 0);
  // Keep the user's current zoom across the axis change (no refit).
  _pvFromZoom = orthoCamera.zoom;
  _pvToZoom = orthoCamera.zoom;
  // Derive the destination orientation by briefly aiming the real camera at the
  // new center, then restore the start state so the tween animates from here.
  const savePos = orthoCamera.position.clone();
  const saveUp = orthoCamera.up.clone();
  orthoCamera.up.copy(_pvToUp);
  orthoCamera.position.copy(_pvToTarget).addScaledVector(dir, PLAN_VIEW_DIST);
  orthoCamera.lookAt(_pvToTarget);
  _pvToQ.copy(orthoCamera.quaternion);
  orthoCamera.position.copy(savePos);
  orthoCamera.up.copy(saveUp);
  orthoCamera.quaternion.copy(_pvFromQ);
  _pvT = 0;
  ctx.setPlanAlign(true);
}
function updatePlanViewTween(): void {
  if (!ctx.planAlignOn()) return;
  _pvT = Math.min(1, _pvT + 0.14);
  const e = _pvT * _pvT * (3 - 2 * _pvT); // smoothstep
  floor2DControls.target.lerpVectors(_pvFromTarget, _pvToTarget, e);
  orthoCamera.zoom = _pvFromZoom + (_pvToZoom - _pvFromZoom) * e;
  orthoCamera.updateProjectionMatrix();
  orthoCamera.quaternion.copy(_pvFromQ).slerp(_pvToQ, e);
  // Keep the camera on the view axis: its backward (+Z local) points from the
  // target toward the camera. Runs after floor2DControls.update() each frame,
  // so this orientation is what actually renders.
  _pvDir.set(0, 0, 1).applyQuaternion(orthoCamera.quaternion);
  orthoCamera.position
    .copy(floor2DControls.target)
    .addScaledVector(_pvDir, PLAN_VIEW_DIST);
  if (_pvT >= 1) {
    orthoCamera.up.copy(_pvToUp); // so floor2DControls.update() re-aims squarely
    orthoCamera.quaternion.copy(_pvToQ);
    floor2DControls.target.copy(_pvToTarget);
    orthoCamera.zoom = _pvToZoom;
    orthoCamera.updateProjectionMatrix();
    ctx.setPlanAlign(false);
  }
}

// Plan-view (2D) corner nav-gizmo: a CLICK on an axis ball snaps the ortho
// camera to that axis-aligned view. Dragging the gizmo does nothing — the only
// drag that rotates is the right-drag swipe. Returns true while it owns the
// press so the floor tools skip it (and the release that follows).
let _planGizmoOwned = false;
let _planGizmoAxis: THREE.Vector3 | null = null;
let _planGizmoMoved = false;
let _planGizmoStartX = 0;
let _planGizmoStartY = 0;
function updatePlanNavGizmo(): boolean {
  updatePlanViewTween();
  if (
    !_planGizmoOwned &&
    isInsideGizmo(desktopMouse.clientX, desktopMouse.clientY) &&
    consumeDesktopPress()
  ) {
    _planGizmoOwned = true;
    _planGizmoMoved = false;
    _planGizmoAxis = pickGizmoAxis(desktopMouse.clientX, desktopMouse.clientY);
    _planGizmoStartX = desktopMouse.clientX;
    _planGizmoStartY = desktopMouse.clientY;
  }
  if (_planGizmoOwned) {
    if (
      Math.hypot(
        desktopMouse.clientX - _planGizmoStartX,
        desktopMouse.clientY - _planGizmoStartY,
      ) > GIZMO_DRAG_THRESH
    ) {
      _planGizmoMoved = true; // a drag → not a click → don't snap
    }
    if (consumeDesktopRelease()) {
      // Snap only on a clean click (no drag) of an axis ball; never −Y (bottom).
      if (!_planGizmoMoved && _planGizmoAxis && _planGizmoAxis.y > -0.9) {
        startPlanViewAlign(_planGizmoAxis);
      }
      _planGizmoOwned = false;
      _planGizmoAxis = null;
    }
  }
  const over = desktopMouse.overCanvas && !desktopMouse.camDragging;
  setGizmoHover(over ? desktopMouse.clientX : -1, over ? desktopMouse.clientY : -1);
  return _planGizmoOwned;
}

// Corner nav-gizmo interaction for the 3D layout view (the perspective orbit
// camera the gizmo mirrors). Mirrors the modeling path: a press in the corner
// region begins an interaction — drag to orbit, or release without moving to
// snap the view down the clicked axis. Returns true while it owns the gesture
// so the floor tools skip this frame. The 2D plan view snaps to axes instead.
function updateFloorNavGizmo(): boolean {
  if (uiState.floorView !== "3d") {
    return updatePlanNavGizmo();
  }
  updateViewTween();
  if (
    !_gizmoDrag.active &&
    isInsideGizmo(desktopMouse.clientX, desktopMouse.clientY) &&
    consumeDesktopPress() // swallow the press so the tools don't also act on it
  ) {
    _vaActive = false;
    _gizmoDrag.active = true;
    _gizmoDrag.moved = false;
    _gizmoDrag.startX = _gizmoDrag.lastX = desktopMouse.clientX;
    _gizmoDrag.startY = _gizmoDrag.lastY = desktopMouse.clientY;
    _gizmoDrag.axis = pickGizmoAxis(desktopMouse.clientX, desktopMouse.clientY);
  }
  const active = _gizmoDrag.active;
  if (active) {
    updateGizmoDrag();
    if (consumeDesktopRelease()) {
      if (!_gizmoDrag.moved && _gizmoDrag.axis) startViewAlign(_gizmoDrag.axis);
      _gizmoDrag.active = false;
      _gizmoDrag.axis = null;
    }
  }
  const showHover =
    desktopMouse.overCanvas &&
    !desktopMouse.camDragging &&
    !(_gizmoDrag.active && _gizmoDrag.moved);
  setGizmoHover(
    showHover ? desktopMouse.clientX : -1,
    showHover ? desktopMouse.clientY : -1,
  );
  return active;
}

// Non-passthrough filter: reduce `picked` to the front-most voxel per column
// along the nearest view axis (the visible surface), so the marquee grabs only
// the first layer instead of everything behind it. `objInv` is the active
// object's world-inverse, used to bring the camera direction into voxel space.
// Exact in an axis-aligned ortho view; a reasonable approximation when angled.
function frontLayerVoxels(
  picked: { ix: number; iy: number; iz: number }[],
  cam: THREE.Camera,
  objInv: THREE.Matrix4,
): void {
  if (picked.length === 0) return;
  cam.getWorldDirection(_mqDir); // world forward (into the scene)
  _mqDir.transformDirection(objInv).negate(); // → voxel space, toward the camera
  const ax = Math.abs(_mqDir.x);
  const ay = Math.abs(_mqDir.y);
  const az = Math.abs(_mqDir.z);
  const ai = ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
  const sign = _mqDir.getComponent(ai) >= 0 ? 1 : -1; // +1: front = max coord
  const coordOf = (v: { ix: number; iy: number; iz: number }) =>
    ai === 0 ? v.ix : ai === 1 ? v.iy : v.iz;
  const colKey = (v: { ix: number; iy: number; iz: number }) =>
    ai === 0 ? `${v.iy},${v.iz}` : ai === 1 ? `${v.ix},${v.iz}` : `${v.ix},${v.iy}`;
  const front = new Map<string, { ix: number; iy: number; iz: number }>();
  for (const v of picked) {
    const k = colKey(v);
    const cur = front.get(k);
    if (!cur || (sign > 0 ? coordOf(v) > coordOf(cur) : coordOf(v) < coordOf(cur))) {
      front.set(k, v);
    }
  }
  picked.length = 0;
  for (const v of front.values()) picked.push(v);
}

// Live marquee (per frame): draw ONLY the screen rect. The voxel selection is
// deliberately NOT recomputed here — the highlight is applied once on release
// (commitDesktopMarquee), so the in-progress box stays visually quiet.
function updateDesktopMarquee(): void {
  if (!_dmActive) {
    desktopMarqueeRect.hide();
    return;
  }
  const sx = _dmStartNdc.x;
  const sy = _dmStartNdc.y;
  const ex = desktopMouse.ndc.x;
  const ey = desktopMouse.ndc.y;
  desktopMarqueeRect.show(
    ((sx + 1) * 0.5) * window.innerWidth,
    ((1 - sy) * 0.5) * window.innerHeight,
    ((ex + 1) * 0.5) * window.innerWidth,
    ((1 - ey) * 0.5) * window.innerHeight,
  );
}

// Commit the marquee on release: select every voxel of the active object whose
// projected screen footprint overlaps the box (any-corner-in-rect box select).
// This is the ONLY place a marquee drag mutates the selection.
function commitDesktopMarquee(): void {
  const sx = _dmStartNdc.x;
  const sy = _dmStartNdc.y;
  const ex = desktopMouse.ndc.x;
  const ey = desktopMouse.ndc.y;
  const x0 = Math.min(sx, ex);
  const x1 = Math.max(sx, ex);
  const y0 = Math.min(sy, ey);
  const y1 = Math.max(sy, ey);
  // Project with the camera that's actually on screen — perspective normally,
  // the synced ortho cam in orthographic mode — so the screen rect lines up.
  const cam = activeCamera();
  cam.updateMatrixWorld();
  cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
  activeObject.group.updateMatrixWorld(true);
  const m = activeObject.group.matrixWorld;
  const picked: { ix: number; iy: number; iz: number }[] = [];
  // A voxel is selected if ANY part of it touches the rect: project its 8
  // corners, take their screen-space bounding box, and test that box for
  // overlap with the marquee rect.
  for (const [ix, iy, iz] of grid.entries()) {
    let vx0 = Infinity;
    let vx1 = -Infinity;
    let vy0 = Infinity;
    let vy1 = -Infinity;
    let anyInFront = false;
    for (let c = 0; c < 8; c++) {
      _mqTmp
        .set(
          (ix + (c & 1)) * CELL_SIZE,
          (iy + ((c >> 1) & 1)) * CELL_SIZE,
          (iz + ((c >> 2) & 1)) * CELL_SIZE,
        )
        .applyMatrix4(m)
        .project(cam);
      if (_mqTmp.z < -1 || _mqTmp.z > 1) continue; // corner outside depth
      anyInFront = true;
      if (_mqTmp.x < vx0) vx0 = _mqTmp.x;
      if (_mqTmp.x > vx1) vx1 = _mqTmp.x;
      if (_mqTmp.y < vy0) vy0 = _mqTmp.y;
      if (_mqTmp.y > vy1) vy1 = _mqTmp.y;
    }
    if (!anyInFront) continue;
    // AABB overlap (rect [x0,x1]×[y0,y1] vs voxel [vx0,vx1]×[vy0,vy1]).
    if (vx0 <= x1 && vx1 >= x0 && vy0 <= y1 && vy1 >= y0) {
      picked.push({ ix, iy, iz });
    }
  }
  if (_dmAlt) frontLayerVoxels(picked, cam, _mqInv.copy(m).invert());
  // Mirror also selects the reflection of each marqueed voxel (filled cells only).
  const sel = mirrorExpandSelection(picked);
  if (_dmMode === "add") {
    selection.replace([..._dmBase, ...sel]); // replace() dedupes by key
  } else if (_dmMode === "subtract") {
    const rm = new Set(sel.map((c) => `${c.ix},${c.iy},${c.iz}`));
    selection.replace(_dmBase.filter((c) => !rm.has(`${c.ix},${c.iy},${c.iz}`)));
  } else {
    selection.replace(sel);
  }
}

// Desktop hover pick + face cursor. No laser line (it would emanate from the
// camera); the face cursor is the pointer.
function updateDesktopPickAndCursor(): void {
  const ray = controllerRays[0];
  if (!ray) return;
  camera.getWorldPosition(_camWorldPosTmp);
  // Off-canvas (or swinging the camera) → no surface pick. The box keeps growing
  // via updateDesktopBox (stable plane projection); other tools follow the live
  // cursor ndc / screen projection. Leaving lastPick non-null here would let
  // handleBoxDrag drive the box from an erratic beyond-frustum floor-mesh hit.
  // Suppressing during a camera drag also keeps the face cursor from flickering
  // across surfaces while you orbit/pan.
  if (!desktopMouse.overCanvas || desktopMouse.camDragging) {
    lastPick[0] = null;
    hideControllerRay(ray);
    return;
  }
  desktopMouseRay(_pickOriginTmp, _pickDirTmp);

  // RESIZE: aim at a wall handle. Pin the grabbed handle while dragging.
  if (uiState.topMode === "resize") {
    lastPick[0] = null;
    if (interaction.kind === "resize_wall" && interaction.controllerIndex === 0) {
      const idx = grabbedHandleIndex(interaction.axis, interaction.sign);
      resizeHotHandle[0] = idx;
      resizeHandleMeshes[idx].getWorldPosition(_offCanvasPosTmp);
      setControllerRayOffCanvas(
        ray,
        _offCanvasPosTmp,
        _camWorldPosTmp,
        _pickOriginTmp.distanceTo(_offCanvasPosTmp),
      );
      ray.line.visible = false;
      return;
    }
    _panelRaycaster.set(_pickOriginTmp, _pickDirTmp);
    const hits = _panelRaycaster.intersectObjects(resizeHandleMeshes, false);
    if (hits.length > 0) {
      resizeHotHandle[0] = resizeHandleMeshes.indexOf(hits[0].object as THREE.Mesh);
      resizeHotDist[0] = hits[0].distance;
      setControllerRayOffCanvas(ray, hits[0].point, _camWorldPosTmp, hits[0].distance);
      ray.line.visible = false;
    } else {
      resizeHotHandle[0] = -1;
      hideControllerRay(ray);
    }
    return;
  }

  const masks = gestureRaycastMasks(0);
  const { pick, group } = multiObjectPick(
    0,
    _pickOriginTmp,
    _pickDirTmp,
    masks.skip,
    masks.include,
    masks.includeBox,
    masks.skipBox,
  );
  if (ray.cursor.parent !== group) group.add(ray.cursor);
  if (pick) {
    lastPick[0] = pick;
    updateControllerRayVisuals(ray, pick);
    ray.line.visible = false;
    // Select + Move don't place per-cell, so hide the face cursor. Freeform
    // draws a freehand line, not a per-cell stamp — same.
    if (
      uiState.topMode === "select" ||
      uiState.topMode === "move" ||
      ((uiState.topMode === "sketch" || uiState.topMode === "paint") &&
        uiState.shapeMode === "freeform")
    ) {
      ray.cursor.visible = false;
    }
    // Deep zoom (perspective): when the eye skims within a few cells of the
    // picked face, the fixed-size, depth-test-off face cursor projects huge and
    // paints over everything — a big white square flashing as you zoom past it.
    // Hide it once that close. (Ortho's cursor is constant-size, so it's exempt.)
    if (!ctx.modelOrthoOn() && pick.distance < CELL_SIZE * gridGroup.scale.x * 3) {
      ray.cursor.visible = false;
    }
  } else {
    lastPick[0] = null;
    hideControllerRay(ray);
  }
}

// Desktop extrude depth driver. Depth tracks the cursor in WORLD space: each
// frame we find the point on the extrude axis (the seed cell's center swept along
// the face normal) closest to the mouse ray, and read off how many cells along it
// that lands — the same axis-drag a translate gizmo uses. This pins the pulled
// face under the cursor regardless of perspective foreshortening, zoom, or aspect.
// The old driver sampled ONE cell's on-screen length once at depth 0, so a face
// angled toward the camera projected short and extruded far faster than the mouse
// moved. A near head-on face — axis pointing at the camera, where the ray↔axis
// closest point runs off to infinity — falls back to a plain vertical screen drag.
const _deStartNdc = new THREE.Vector2();
const _deRayO = new THREE.Vector3();
const _deRayD = new THREE.Vector3();
const _deAxisO = new THREE.Vector3();
const _deAxisU = new THREE.Vector3();
const _deW0 = new THREE.Vector3();
let _deActive = false;
let _deHeadOn = false;
let _deStartT = 0; // axis parameter (in cells) at press — the drag's zero point

// Parameter, in cells, along the extrude axis _deAxisO + t*_deAxisU of the point
// closest to the current mouse ray (_deRayO + s*_deRayD, _deRayD normalized).
// _deAxisU is one CELL_SIZE along the normal, so t is already a cell count.
// Returns null when the axis sits within ~8° of the ray (near head-on), where the
// closest point is ill-conditioned and shoots to infinity.
function closestAxisCells(): number | null {
  const b = _deRayD.dot(_deAxisU);
  const c = _deAxisU.dot(_deAxisU); // (world length of one cell)^2 — always > 0
  const denom = c - b * b; // = c * sin^2(angle between ray and axis)
  if (denom < c * 0.02) return null; // sin < ~0.14 → angle < ~8°, near head-on
  _deW0.copy(_deRayO).sub(_deAxisO);
  const d = _deRayD.dot(_deW0);
  const e = _deAxisU.dot(_deW0);
  return (e - b * d) / denom;
}

function updateDesktopExtrude(): void {
  if (
    interaction.kind !== "extrude_draw" &&
    interaction.kind !== "extrude_erase" &&
    interaction.kind !== "extrude_recolor"
  ) {
    _deActive = false;
    return;
  }
  const state = interaction;
  const gObj = gestureObject();
  gObj.group.updateMatrixWorld(true);

  // World-space extrude axis: the seed-cell center, and a per-cell step vector
  // (the center swept one CELL_SIZE along the face normal, minus the center).
  _deAxisO.copy(state.axisAnchor).applyMatrix4(gObj.group.matrixWorld);
  _deAxisU
    .set(
      state.axisAnchor.x + state.normal[0] * CELL_SIZE,
      state.axisAnchor.y + state.normal[1] * CELL_SIZE,
      state.axisAnchor.z + state.normal[2] * CELL_SIZE,
    )
    .applyMatrix4(gObj.group.matrixWorld)
    .sub(_deAxisO);
  desktopMouseRay(_deRayO, _deRayD);

  if (!_deActive) {
    const t0 = closestAxisCells();
    _deHeadOn = t0 === null;
    if (_deHeadOn) _deStartNdc.set(desktopMouse.ndc.x, desktopMouse.ndc.y);
    else _deStartT = t0!; // grip the cursor's start point on the axis as zero
    _deActive = true;
  }

  let cells: number;
  if (_deHeadOn) {
    cells = (desktopMouse.ndc.y - _deStartNdc.y) * 6; // vertical drag, up = out
  } else {
    const t = closestAxisCells();
    if (t === null) return; // grazed head-on this frame — hold the last depth
    cells = t - _deStartT;
  }
  // ERASE and RECOLOR both peel inward — drag toward the object grows depth.
  if (state.kind !== "extrude_draw") cells = -cells;
  // +1 baseline: startExtrude applied the first layer on press.
  applyExtrudeTargetDepth(1 + Math.round(cells), gObj.grid, cellBounds);
}

// Desktop box FALLBACK — only when the cursor is off every surface (pick
// missed). handleBoxDrag already drives the box from the hovered floor/wall/
// voxel cell (so dragging up a wall extrudes in 3D); this fallback kicks in
// when the ray hits nothing (cursor past the floor edge / off-canvas) and
// fills the box to the volume edge on its start plane instead of freezing.
const _dbRayO = new THREE.Vector3();
const _dbRayD = new THREE.Vector3();
const _dbInv = new THREE.Matrix4();
const _dbHit = new THREE.Vector3();
const _dbCamLocal = new THREE.Vector3();

// Build the object-local cursor ray (_dbRayO/_dbRayD) from an NDC point.
// _dbInv (object world-inverse) must already be set. Uses the ACTIVE camera and
// handles orthographic mode (parallel rays from the near plane) — otherwise the
// cursor projection is wrong whenever the ortho model camera is in use.
function boxRayFromNdc(nx: number, ny: number): void {
  const cam = activeCamera();
  if (cam instanceof THREE.OrthographicCamera) {
    _pickOriginTmp.set(nx, ny, -1).unproject(cam);
    cam.getWorldDirection(_pickDirTmp);
  } else {
    cam.getWorldPosition(_pickOriginTmp);
    _pickDirTmp.set(nx, ny, 0.5).unproject(cam).sub(_pickOriginTmp).normalize();
  }
  _dbRayO.copy(_pickOriginTmp).applyMatrix4(_dbInv);
  _dbRayD.copy(_pickDirTmp).transformDirection(_dbInv).normalize();
}

// Ray (object-local _dbRayO/_dbRayD) ∩ the axis-aligned plane at world
// coordinate `world` on `axis`. Writes _dbHit; false if no front hit.
function rayToWorldPlane(axis: number, world: number): boolean {
  const o = _dbRayO.getComponent(axis);
  const d = _dbRayD.getComponent(axis);
  if (Math.abs(d) < 1e-6) return false;
  const t = (world - o) / d;
  if (t <= 0) return false;
  _dbHit.copy(_dbRayO).addScaledVector(_dbRayD, t);
  return true;
}

// Same, at the CENTRE of cell row `coord` on `axis` — the stable sampling the
// freeform lasso projects onto (its plane axis wants the row, not a face).
function rayToCellPlane(axis: number, coord: number): boolean {
  return rayToWorldPlane(axis, (coord + 0.5) * CELL_SIZE);
}

function updateDesktopBox(): void {
  if (
    interaction.kind !== "box_draw" &&
    interaction.kind !== "box_erase" &&
    interaction.kind !== "box_recolor" &&
    interaction.kind !== "circle_draw" &&
    interaction.kind !== "circle_erase" &&
    interaction.kind !== "circle_recolor"
  ) {
    return;
  }
  // Draw is driven by handleBox/CircleDrag for any pick (voxel OR room surface);
  // only a null pick (cursor over empty space) falls back to the plane projection
  // below. Erase/Recolor are driven there only for a VOXEL pick — a surface pick
  // yields a null target (nothing under the cursor to erase/recolor) and would
  // otherwise freeze the box — so for those, run the plane projection whenever
  // the cursor isn't on a voxel, keeping the box growing to the cursor.
  const draw =
    interaction.kind === "box_draw" || interaction.kind === "circle_draw";
  const pick = lastPick[0];
  if (draw ? pick !== null : pick?.kind === "voxel") return;
  const state = interaction;
  const isCircle =
    state.kind === "circle_draw" ||
    state.kind === "circle_erase" ||
    state.kind === "circle_recolor";
  const gObj = gestureObject();
  gObj.group.updateMatrixWorld(true);
  _dbInv.copy(gObj.group.matrixWorld).invert();
  boxRayFromNdc(desktopMouse.ndc.x, desktopMouse.ndc.y);
  camera.getWorldPosition(_dbCamLocal);
  _dbCamLocal.applyMatrix4(_dbInv);

  // "If the room extended to the cursor, what surface is it over?" Pick the
  // NEAREST surface the ray reaches among the FLOOR (infinite, only when the
  // ray descends to it) and the FAR walls (those the camera is inside of,
  // treated as extending up from the floor). This decides floor↔wall every
  // frame — drag across → floor; drag past a far wall → climb; descend a wall
  // past its base → back to the floor. Never picks a near wall (camera outside
  // it), so dragging toward yourself just extends the floor.
  const EPS = 1e-6;
  const floorYm = cellBounds.minIy * CELL_SIZE;
  let bestT = Infinity;
  let bestAxis = -1;
  let bestCoord = 0;
  // World coordinate of the chosen surface's FACE on bestAxis. The in-plane axes
  // are read from the ray∩FACE point (below) — NOT the cell-centre — so this
  // projection lands on the exact same cell surfaceHitToCell picks for a real
  // surface hit. If it read the cell-centre instead, the half-cell-deeper hit
  // point floors to a neighbouring cell on the in-plane axes; the pick then
  // flip-flops surface↔null every frame between the two drivers and the box's
  // leading layer strobes (drawn, restored, drawn…) — a translucent "ghost".
  let bestFaceWorld = 0;

  if (_dbRayD.y < -EPS) {
    const t = ((cellBounds.minIy + 0.5) * CELL_SIZE - _dbRayO.y) / _dbRayD.y;
    if (t > 0) {
      bestT = t;
      bestAxis = 1;
      bestCoord = cellBounds.minIy;
      bestFaceWorld = cellBounds.minIy * CELL_SIZE;
    }
  }
  const tryWall = (a: 0 | 2, coordCell: number, faceWorld: number, camInside: boolean) => {
    if (!camInside) return; // near wall → not a surface the camera sees
    const o = a === 0 ? _dbRayO.x : _dbRayO.z;
    const d = a === 0 ? _dbRayD.x : _dbRayD.z;
    if (Math.abs(d) < EPS) return;
    const t = ((coordCell + 0.5) * CELL_SIZE - o) / d;
    if (t <= 0 || t >= bestT) return;
    if (_dbRayO.y + _dbRayD.y * t < floorYm - EPS) return; // below the wall base
    bestT = t;
    bestAxis = a;
    bestCoord = coordCell;
    bestFaceWorld = faceWorld;
  };
  const cs = CELL_SIZE;
  tryWall(0, cellBounds.minIx, cellBounds.minIx * cs, _dbCamLocal.x > cellBounds.minIx * cs);
  tryWall(0, cellBounds.maxIx - 1, cellBounds.maxIx * cs, _dbCamLocal.x < cellBounds.maxIx * cs);
  tryWall(2, cellBounds.minIz, cellBounds.minIz * cs, _dbCamLocal.z > cellBounds.minIz * cs);
  tryWall(2, cellBounds.maxIz - 1, cellBounds.maxIz * cs, _dbCamLocal.z < cellBounds.maxIz * cs);

  if (bestAxis < 0) return; // ray over nothing (e.g. pointing at the sky) → hold
  // Sample the in-plane axes at the surface FACE (matches surfaceHitToCell), not
  // the cell-centre — see bestFaceWorld above. The plane axis itself is pinned to
  // bestCoord below, so its component of _dbHit is unused.
  rayToWorldPlane(bestAxis, bestFaceWorld); // fills _dbHit (t > 0 guaranteed)

  // Far corner: the chosen axis sits on its plane; the other two come from the
  // hit. All clamped to the volume.
  const lo = [cellBounds.minIx, cellBounds.minIy, cellBounds.minIz];
  const hi = [cellBounds.maxIx, cellBounds.maxIy, cellBounds.maxIz];
  let fx = state.startCell.ix;
  let fy = state.startCell.iy;
  let fz = state.startCell.iz;
  for (let a = 0; a < 3; a++) {
    const raw =
      a === bestAxis ? bestCoord : Math.floor(_dbHit.getComponent(a) / CELL_SIZE);
    const c = Math.max(lo[a], Math.min(hi[a] - 1, raw));
    if (a === 0) fx = c;
    else if (a === 1) fy = c;
    else fz = c;
  }
  if (isCircle) {
    setCircleRim(fx, fy, fz, gObj.grid, cellBounds, uiState.currentPaletteIndex);
  } else {
    setBoxFarCorner(fx, fy, fz, gObj.grid, cellBounds, uiState.currentPaletteIndex);
  }
}

// Is the freeform polygon lasso the active tool? (Sketch/Paint, Model mode.)
function freeformShapeActive(): boolean {
  return (
    uiState.mode === "modeling" &&
    uiState.shapeMode === "freeform" &&
    (uiState.topMode === "sketch" || uiState.topMode === "paint")
  );
}

// Project the cursor onto the in-progress polygon's FIXED drawing plane; on
// success _dbHit holds the hit in that object's local frame. False = off-plane.
function freeformPlaneHitInto(): boolean {
  const group = freeformPolyGroup();
  const plane = freeformPolyPlane();
  if (!group || !plane) return false;
  group.updateMatrixWorld(true);
  _dbInv.copy(group.matrixWorld).invert();
  boxRayFromNdc(desktopMouse.ndc.x, desktopMouse.ndc.y);
  return rayToCellPlane(plane.planeAxis, plane.planeCoord);
}

// A press while the freeform lasso is active: start the polygon (first vertex),
// or drop / grab / close on the one already in progress.
function handleFreeformPress(): void {
  const action: "draw" | "erase" | "recolor" =
    uiState.topMode === "paint" ? "recolor" : uiState.actionMode;
  if (!freeformPolyActive()) {
    const pick = lastPick[0];
    if (!pick) return; // need a surface/voxel to anchor the drawing plane
    const target = ctx.freeformResolveTarget(0);
    if (!target) return;
    if (!beginFreeformPoly(pick, 0, action, target.grid, target.group)) return;
    // Drop the first vertex exactly under the press.
    if (freeformPlaneHitInto()) {
      dropFreeformVertex(_dbHit.x, _dbHit.y, _dbHit.z, cellBounds);
    }
    return;
  }
  if (!freeformPlaneHitInto()) return; // pressed off the plane — ignore
  if (freeformPress(_dbHit.x, _dbHit.y, _dbHit.z, cellBounds) === "close") {
    ctx.closeFreeform(); // fill + select inside one transaction (host-owned)
  }
}

// Desktop freeform driver: project the cursor onto the FIXED drawing plane the
// polygon started on (unlike box/circle, which re-pick a surface each frame).
// Runs every frame a polygon is being built, so the rubber-band + handle hover
// follow the cursor across the open plane — and a grabbed vertex tracks the drag.
function updateDesktopFreeform(): void {
  if (!freeformPolyActive()) return;
  if (!freeformShapeActive()) {
    cancelFreeformPoly(); // switched tool/mode mid-draw — abandon the outline
    return;
  }
  if (!freeformPlaneHitInto()) return; // cursor off the plane → keep the last point
  const drag = freeformDragVert();
  if (drag !== null && desktopMouse.lmb) {
    moveFreeformVertex(drag, _dbHit.x, _dbHit.y, _dbHit.z, cellBounds);
  } else {
    setFreeformCursor(_dbHit.x, _dbHit.y, _dbHit.z, cellBounds);
  }
}

// Preview for the freeform lasso: the outline (placed vertices + a rubber-band to
// the cursor) plus a dot at each vertex, drawn over everything while building (no
// voxels appear until close). Re-parented to the target object's group each frame
// so its object-local points line up with that object's voxels. The outline uses
// fat-line rendering (Line2) so its width is honored — a plain THREE.Line is
// clamped to 1px in WebGL and is hard to see.
const freeformPreviewMat = new LineMaterial({
  color: 0xffd633, // bright yellow — high contrast against the canvas + voxels
  linewidth: 4, // screen pixels
  transparent: true,
  depthTest: false, // always drawn on top
});
freeformPreviewMat.resolution.set(window.innerWidth, window.innerHeight);
const freeformPreview = new Line2(new LineGeometry(), freeformPreviewMat);
freeformPreview.visible = false;
freeformPreview.renderOrder = 20;
freeformPreview.frustumCulled = false;

// Vertex handle dots (and a brighter, larger highlight for the one under the
// cursor — the grab/close target).
const freeformDotsMat = new THREE.PointsMaterial({
  color: 0xffd633,
  size: 9,
  sizeAttenuation: false,
  transparent: true,
  depthTest: false,
});
const freeformDots = new THREE.Points(new THREE.BufferGeometry(), freeformDotsMat);
freeformDots.visible = false;
freeformDots.renderOrder = 21;
freeformDots.frustumCulled = false;
const freeformHotDotMat = new THREE.PointsMaterial({
  color: 0xffffff,
  size: 14,
  sizeAttenuation: false,
  transparent: true,
  depthTest: false,
});
const freeformHotDot = new THREE.Points(new THREE.BufferGeometry(), freeformHotDotMat);
freeformHotDot.visible = false;
freeformHotDot.renderOrder = 22;
freeformHotDot.frustumCulled = false;

function updateFreeformPreview(): void {
  const g = freeformPolyGroup();
  if (!freeformPolyActive() || !g) {
    freeformPreview.visible = false;
    freeformDots.visible = false;
    freeformHotDot.visible = false;
    return;
  }
  freeformPreviewMat.resolution.set(window.innerWidth, window.innerHeight);

  // Outline. Rebuild the geometry from scratch each frame: reusing setPositions
  // on a growing Line2 geometry doesn't reliably bump its instance count, so the
  // line stops extending past the first segments.
  const pts = freeformPreviewPositions();
  if (pts.length >= 6) {
    if (freeformPreview.parent !== g) g.add(freeformPreview);
    const geom = new LineGeometry();
    geom.setPositions(pts);
    freeformPreview.geometry.dispose();
    freeformPreview.geometry = geom;
    freeformPreview.visible = true;
  } else {
    freeformPreview.visible = false;
  }

  // Vertex dots.
  const verts = freeformVertexPositions();
  if (verts.length >= 3) {
    if (freeformDots.parent !== g) g.add(freeformDots);
    const dg = new THREE.BufferGeometry();
    dg.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
    freeformDots.geometry.dispose();
    freeformDots.geometry = dg;
    freeformDots.visible = true;
  } else {
    freeformDots.visible = false;
  }

  // Highlight the vertex under the cursor (grab/close affordance).
  const hover = freeformHoverVertex();
  if (hover !== null && verts.length >= (hover + 1) * 3) {
    if (freeformHotDot.parent !== g) g.add(freeformHotDot);
    const hg = new THREE.BufferGeometry();
    hg.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(verts.slice(hover * 3, hover * 3 + 3), 3),
    );
    freeformHotDot.geometry.dispose();
    freeformHotDot.geometry = hg;
    freeformHotDot.visible = true;
  } else {
    freeformHotDot.visible = false;
  }
}

// Desktop resize driver — same screen-projection fix as extrude: move the
// dragged wall edge by mouse drag projected onto the on-screen direction of
// the wall axis (in metres). Robust even when the wall faces the camera.
const _drStartNdc = new THREE.Vector2();
const _drAxisNdc = new THREE.Vector2(); // metres-per-NDC vector along the axis
const _drAnchorNdc = new THREE.Vector2();
const _drAnchorWorld = new THREE.Vector3();
const _drAxisWorld = new THREE.Vector3();
const _drTmp = new THREE.Vector3();
const _drQuat = new THREE.Quaternion();
let _drActive = false;
function updateDesktopResize(): void {
  if (interaction.kind !== "resize_wall") {
    _drActive = false;
    return;
  }
  const state = interaction;
  const idx = grabbedHandleIndex(state.axis, state.sign);
  if (idx < 0) return;
  if (!_drActive) {
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    gridGroup.updateMatrixWorld(true);
    _drStartNdc.set(desktopMouse.ndc.x, desktopMouse.ndc.y);
    resizeHandleMeshes[idx].getWorldPosition(_drAnchorWorld);
    _drTmp.copy(_drAnchorWorld).project(camera);
    _drAnchorNdc.set(_drTmp.x, _drTmp.y);
    // World step for +1 gridGroup-local metre along the axis.
    gridGroup.getWorldQuaternion(_drQuat);
    _drAxisWorld
      .set(state.axis === 0 ? 1 : 0, state.axis === 1 ? 1 : 0, state.axis === 2 ? 1 : 0)
      .applyQuaternion(_drQuat)
      .multiplyScalar(gridGroup.scale.x);
    _drTmp.copy(_drAnchorWorld).add(_drAxisWorld).project(camera);
    const rawX = _drTmp.x - _drAnchorNdc.x;
    const rawY = _drTmp.y - _drAnchorNdc.y;
    const len2 = rawX * rawX + rawY * rawY;
    if (len2 < 1e-10) _drAxisNdc.set(0, 3); // head-on: vertical drag
    else _drAxisNdc.set(rawX / len2, rawY / len2);
    _drActive = true;
  }
  const dx = desktopMouse.ndc.x - _drStartNdc.x;
  const dy = desktopMouse.ndc.y - _drStartNdc.y;
  const metres = dx * _drAxisNdc.x + dy * _drAxisNdc.y;
  // No content clamp: shrinking past voxels is allowed; applyResizeExtent →
  // applyVolumeBounds enforces the 1-cell minimum and clips what's left outside.
  applyResizeExtent(state.axis, state.sign, state.startEdge + metres);
}

// Desktop move-selection driver (MagicaVoxel-style). The clicked face's
// normal is excluded; the move is constrained to the perpendicular plane.
// The drag direction picks WHICH of the two in-plane axes to slide along,
// locked once the drag commits — so a Z-face + vertical drag moves in Y, a
// Z-face + horizontal drag moves in X, etc. Magnitude uses screen
// projection (one cell's on-screen length of drag = one cell), which is
// robust even when an axis foreshortens toward the camera.
const _dmoGrab = new THREE.Vector3();
const _dmoStep = new THREE.Vector3();
const _dmoStartNdc = new THREE.Vector2();
const _dmoNdcGrab = new THREE.Vector2();
// Per world-axis (0/1/2): cells-per-NDC vector + normalized screen dir.
const _dmoCellAxis = [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()];
const _dmoDir = [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()];
const _dmoUsable = [false, false, false];
let _dmoActive = false;
let _dmoAxis = -1; // locked move axis (-1 = not yet committed)
const DMO_LOCK_NDC = 0.015; // drag distance before the axis locks

function inPlaneAxes(normalAxis: 0 | 1 | 2): [number, number] {
  return normalAxis === 0 ? [1, 2] : normalAxis === 1 ? [0, 2] : [0, 1];
}

// Dominant GRID-LOCAL axis (0/1/2) of the on-screen view direction — the plane
// normal for the 2D-orthographic free move. In ortho the camera looks straight
// down a grid axis, so this is exact; computed in grid-local space so a rotated
// canvas still resolves to the right cell axes.
const _dmoViewDir = new THREE.Vector3();
const _dmoGridQuat = new THREE.Quaternion();
function viewPlaneNormalAxis(): 0 | 1 | 2 {
  activeCamera().getWorldDirection(_dmoViewDir);
  gridGroup.getWorldQuaternion(_dmoGridQuat);
  _dmoViewDir.applyQuaternion(_dmoGridQuat.invert()); // world dir → grid-local
  const ax = Math.abs(_dmoViewDir.x);
  const ay = Math.abs(_dmoViewDir.y);
  const az = Math.abs(_dmoViewDir.z);
  return ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
}

function updateDesktopMove(): void {
  if (interaction.kind !== "move_selection") {
    _dmoActive = false;
    _dmoAxis = -1;
    return;
  }
  const state = interaction;
  // In the 2D orthographic view, move FREELY within the plane you're looking at
  // (both in-plane axes at once), using the view axis as the plane normal — so
  // looking down Z lets you slide a selection anywhere in XY. In 3D (perspective)
  // keep the MagicaVoxel-style single-axis lock within the grabbed face's plane.
  const free2D = uiState.mode === "modeling" && ctx.modelOrthoOn();
  const normalAxis = free2D ? viewPlaneNormalAxis() : state.faceNormalAxis;
  const [axA, axB] = inPlaneAxes(normalAxis);
  // Project with the camera that's actually on screen (the synced ortho cam in
  // orthographic mode) so the drag→cell mapping matches what the user sees.
  const cam = activeCamera();

  if (!_dmoActive) {
    cam.updateMatrixWorld();
    gridGroup.updateMatrixWorld(true);
    _dmoStartNdc.copy(desktopMouse.ndc);
    _dmoGrab
      .copy(state.startGrabPosGrid)
      .applyMatrix4(gridGroup.matrixWorld)
      .project(cam);
    _dmoNdcGrab.set(_dmoGrab.x, _dmoGrab.y);
    for (const ax of [axA, axB]) {
      _dmoStep.copy(state.startGrabPosGrid);
      _dmoStep.setComponent(ax, _dmoStep.getComponent(ax) + CELL_SIZE);
      _dmoStep.applyMatrix4(gridGroup.matrixWorld).project(cam);
      const rx = _dmoStep.x - _dmoNdcGrab.x;
      const ry = _dmoStep.y - _dmoNdcGrab.y;
      const len2 = rx * rx + ry * ry;
      if (len2 < 1e-8) {
        _dmoUsable[ax] = false; // axis points at the camera — unmovable here
        _dmoCellAxis[ax].set(0, 0);
        _dmoDir[ax].set(0, 0);
      } else {
        _dmoUsable[ax] = true;
        _dmoCellAxis[ax].set(rx / len2, ry / len2); // drag · this = cells
        const len = Math.sqrt(len2);
        _dmoDir[ax].set(rx / len, ry / len);
      }
    }
    _dmoActive = true;
    _dmoAxis = -1;
  }

  const dragX = desktopMouse.ndc.x - _dmoStartNdc.x;
  const dragY = desktopMouse.ndc.y - _dmoStartNdc.y;

  if (free2D) {
    // Free planar move: drive BOTH in-plane axes straight from the cursor (no
    // axis lock) so the selection follows the mouse anywhere in the view plane.
    const off: [number, number, number] = [
      state.currentOffset.dx,
      state.currentOffset.dy,
      state.currentOffset.dz,
    ];
    if (_dmoUsable[axA]) {
      off[axA] = dragX * _dmoCellAxis[axA].x + dragY * _dmoCellAxis[axA].y;
    }
    if (_dmoUsable[axB]) {
      off[axB] = dragX * _dmoCellAxis[axB].x + dragY * _dmoCellAxis[axB].y;
    }
    setMoveOffset(off[0], off[1], off[2], grid, cellBounds, selection);
    return;
  }

  if (_dmoAxis < 0) {
    if (dragX * dragX + dragY * dragY < DMO_LOCK_NDC * DMO_LOCK_NDC) return;
    const sa = _dmoUsable[axA]
      ? Math.abs(dragX * _dmoDir[axA].x + dragY * _dmoDir[axA].y)
      : -1;
    const sb = _dmoUsable[axB]
      ? Math.abs(dragX * _dmoDir[axB].x + dragY * _dmoDir[axB].y)
      : -1;
    if (sa < 0 && sb < 0) return; // both axes degenerate
    _dmoAxis = sa >= sb ? axA : axB;
  }

  const cells =
    dragX * _dmoCellAxis[_dmoAxis].x + dragY * _dmoCellAxis[_dmoAxis].y;
  setMoveOffset(
    _dmoAxis === 0 ? cells : state.currentOffset.dx,
    _dmoAxis === 1 ? cells : state.currentOffset.dy,
    _dmoAxis === 2 ? cells : state.currentOffset.dz,
    grid,
    cellBounds,
    selection,
  );
}

  return {
    // Per-frame desktop updates (render loop, desktop + floor branches).
    updateDesktopPointer,
    updateDesktopPickAndCursor,
    updateDesktopMarquee,
    updateDesktopExtrude,
    updateDesktopBox,
    updateDesktopFreeform,
    updateFreeformPreview,
    updateDesktopResize,
    updateDesktopMove,
    updateGizmoDrag,
    updateViewTween,
    updateFloorNavGizmo,
    applyReferenceDrag,
    // View alignment + projection.
    startViewAlign,
    startPlanViewAlign,
    setModelOrthographic,
    cancelViewAlign: () => {
      _vaActive = false;
    },
    layoutBounds,
    // Shared state peeked at by main's selection / render logic.
    gizmoDrag: _gizmoDrag,
    freeformPreview,
    connectedObject,
  };
}
